import * as Business            from '../business';
import * as errors              from '../util/errors';
import * as Forms               from '../components/forms';
import * as User                from '../user';
import { compose }              from 'redux';
import { connect }              from 'react-redux';
import { format }               from 'libphonenumber-js';
import { twilioFailureReasons } from './hosted_number_page';
import { uploadThankYouImage }  from '../util/file_uploader';
import classnames               from 'classnames';
import EditableLineItem         from '../components/editable_line_item';
import FetchFromServer          from '../components/fetch_from_server';
import Link                     from '../components/link';
import ms                       from 'ms';
import Navbar                   from '../components/navbar';
import React                    from 'react';
import restrictAccess           from '../components/restrict_access';
import SaveOnChange             from '../components/save_on_change';
import UploadImage              from '../components/upload_image';


export default connect(
  (state, { businessID }) => ({
    businessID,
    business:       Business.getBusiness(state, businessID),
    defaults:       Business.getDefaults(state, businessID),
    statuses:       Business.getBusinessStatuses(state, businessID),
    settings:       Business.getSettings(state, businessID),
    isAdmin:        User.isAdmin(state, businessID),
    thankYouRepeat: Business.getThankYouRepeat(state, businessID),
    profile:        User.getProfile(state),
    smsNumber:      Business.getConnectedPhoneNumber(state, businessID),
    hostedNumber:   Business.getHostedNumber(state, businessID),
    twilioNumber:   Business.getTwilioNumber(state, businessID),
    isHostedSMS:    Business.isHostedNumberComplete(state, businessID)
  }),
  (dispatch, { businessID }) => ({
    load:           compose(dispatch, Business.loadBusiness, () => (businessID)),
    saveDelivery:   compose(dispatch, Business.setSMSFollowUp, enabled => ({ businessID, enabled })),
    saveClassify:   compose(dispatch, Business.setClassify, classifyOption => ({ businessID, classifyOption })),
    saveRepeat:     compose(dispatch, Business.setThankYouRepeat, repeat => ({ businessID, repeat })),
    saveSchedule:   compose(dispatch, Business.setSchedule, params => ({ businessID, ...params })),
    sendSMSPreview: compose(dispatch, Business.sendSMSPreview, params => ({ businessID, ...params })),
    showToast:      compose(dispatch, errors.showToastError),
    upload:         compose(dispatch, uploadThankYouImage)
  })
)(restrictAccess(MessagingPage, ({ isAdmin }) => isAdmin));


export function MessagingPage({
  businessID,
  business,
  settings,
  smsNumber,
  hostedNumber,
  twilioNumber,
  isHostedSMS,
  thankYouRepeat,
  saveDelivery,
  saveClassify,
  saveRepeat,
  statuses,
  load,
  saveSchedule,
  defaults,
  showToast,
  sendSMSPreview,
  profile,
  upload
}) {
  return (
    <React.Fragment>
      <Navbar title="Messaging"/>
      <FetchFromServer statuses={statuses} load={load} className={classnames('settings', 'messaging')}>
        {settings?.twilio && (
          <FollowUp
            businessID={businessID}
            saveDelivery={saveDelivery}
            smsNumber={smsNumber}
            twilio={settings.twilio}/>
        )}

        {settings?.canChangeClassify && (
          <FeedbackAndReviews
            businessID={businessID}
            saveClassify={saveClassify}
            classify={settings.classify}/>
        )}

        <ThankYouRepeat
          saveRepeat={saveRepeat}
          defaultThankYouRepeat={thankYouRepeat}/>

        {settings?.schedule && (
          <SMS
            save={saveSchedule}
            schedule={settings.schedule}
            defaults={defaults}
            showToast={showToast}
            sendSMSPreview={sendSMSPreview}
            profile={profile}
            upload={upload}
            hostedNumber={hostedNumber}
            isHostedSMS={isHostedSMS}
          />
        )}

        {business?.contact?.phone && (
          <PhoneNumberSection {...{ business, smsNumber, hostedNumber, twilioNumber, isHostedSMS }}/>
        )}
      </FetchFromServer>
    </React.Fragment>
  );
}

function FollowUp({ businessID, smsNumber, saveDelivery, twilio, ...form }) {
  async function saveFollowUpMessaging({ target }) {
    const { value } = target;
    await saveDelivery(value === 'sms');
  }

  const className = classnames('list', 'media-list', 'block');

  return (
    <SaveOnChange className={className} save={saveFollowUpMessaging}>
      {onChange => (
        <React.Fragment>
          <ul>
            <li className="list-group-title">Review request method</li>
            {smsNumber && (
              <Forms.Radio
                {...form}
                label="Text message and email"
                name="twilio.followUp"
                value="sms"
                onChange={onChange}
                subtitle="Initial review requests will be sent through text. Email will still be used for review follow ups, or if text messaging fails"
                defaultChecked={twilio.followUp}
              />
            )}
            <Forms.Radio
              {...form}
              label="Email only"
              name="twilio.followUp"
              value="email"
              onChange={onChange}
              subtitle="Initial review requests and review follow ups will be sent through email"
              defaultChecked={!twilio.followUp}
            />
          </ul>
          <Instructions businessID={businessID} followUp={twilio.followUp}/>
        </React.Fragment>
      )}
    </SaveOnChange>
  );
}

function FeedbackAndReviews({ saveClassify, classify, ...form }) {
  async function saveClassifySetting({ target }) {
    const { value } = target;
    await saveClassify(value);
  }

  const className = classnames('list', 'media-list', 'block');

  return (
    <SaveOnChange className={className} save={saveClassifySetting}>
      {onChange => (
        <ul>
          <li className="list-group-title">Customer Feedback & Reviews</li>
          {classify && (
            <Forms.Radio
              {...form}
              label="Ask for feedback then review"
              name="classify"
              value="yesno"
              onChange={onChange}
              subtitle="A simple Yes or No gets you feedback faster. You can use this feedback to understand how your business is doing and increase customer satisfaction."
              defaultChecked={classify === 'yesno'}
            />
          )}
          <Forms.Radio
            {...form}
            label="Ask for review directly"
            name="classify"
            value="none"
            onChange={onChange}
            subtitle="Send customers straight to Google, Facebook, etc."
            defaultChecked={classify === 'none'}
          />
          <Forms.Radio
            {...form}
            label="Net Promoter Score and review"
            name="classify"
            value="nps"
            onChange={onChange}
            subtitle="Net Promoter Score helps you better understand your customers by measuring customer satisfactions."
            defaultChecked={classify === 'nps'}
          />
        </ul>
      )}
    </SaveOnChange>
  );
}


const thankYouImagePresets = [
  'https://attachment.broadly.com/messaging/sms/presets/v1.png',
  'https://attachment.broadly.com/messaging/sms/presets/v3.png',
  'https://attachment.broadly.com/messaging/sms/presets/v6.png',
  'https://attachment.broadly.com/messaging/sms/presets/v7.png'
];

function SMS({ save, sendSMSPreview, schedule, defaults, showToast, profile, upload, isHostedSMS, hostedNumber }) {
  async function handleSendSMSPreview({ body = schedule.thankYou.sms.body, imageURL = schedule.thankYou.sms.imageURL }) {
    const userCanReceiveSMS = !!profile.phone;
    if (userCanReceiveSMS) {
      await sendSMSPreview({ body, imageURL });

      const userPhone = profile.phone && format(profile.phone, 'National');
      showToast(`We sent a preview SMS to your phone number ${userPhone}`);
    } else
      showToast('Add a phone number to your profile to send yourself a preview.');
  }

  const canUploadSMSImage = isHostedSMS ? hostedNumber.mms : true;

  return (
    <section className="list inset" aria-label="SMS">
      {thankYouImagePresets.map((image, i) => <PreloadImage key={i} href={image}/>)}
      <ul>
        <li className="list-group-title">SMS</li>

        {
          canUploadSMSImage && (<EditableUploadImageLineItem
            title="Image"
            value={schedule.thankYou.sms.imageURL}
            save={imageURL => save({ thankYou: { sms: { imageURL } } })}
            upload={upload}
            showToast={showToast}
            onSendPreview={currentValue => handleSendSMSPreview({ imageURL: currentValue })}
          />)
        }
        <EditableLineItem
          title="Text"
          value={schedule.thankYou.sms.body}
          defaultValue={defaults.schedule.thankYou.sms.body}
          save={body => save({ thankYou: { sms: { body } } })}
          showToast={showToast}
        >
          <EditableLineItem.Editing>
            <EditableLineItem.Buttons>
              {({ currentValue }) => (
                <a className="button" onClick={() => handleSendSMSPreview({ body: currentValue })}>Send Preview</a>
              )}
            </EditableLineItem.Buttons>
          </EditableLineItem.Editing>
        </EditableLineItem>
      </ul>
    </section>
  );
}

function PreloadImage({ href }) {
  return (
    <link rel="preload" href={href} as="image"/>
  );
}

function ThankYouRepeat({ defaultThankYouRepeat, saveRepeat, ...form }) {
  async function saveFrequency({ target }) {
    const { value } = target;
    await saveRepeat(value);
  }
  const className = classnames('list', 'block', 'thank-you-repeat');
  const options   = {
    [ms('30d')]: '30 days',
    [ms('60d')]: '60 days',
    [ms('90d')]: '90 days'
  };

  return (
    <SaveOnChange className={className} save={saveFrequency}>
      {onChange => (
        <ul>
          <ThankYouRepeatInput
            form={form}
            label="Review Request Frequency"
            description="Wait at least these many days before asking repeat customer for review/feedback"
            onChange={onChange}
            options={options}
            value={defaultThankYouRepeat}/>
        </ul>
      )}
    </SaveOnChange>
  );
}

function ThankYouRepeatInput({ form, label, description, onChange, value, options }) {
  return (
    <li className="item-content">
      <div className="item-inner">
        <div className="item-title">
          <span>{label}</span>
          <p className="item-subtitle">{description}</p>
        </div>
        <ul className="item-after">
          <Forms.Select
            {...form}
            name="schedule.thankYouRepeat"
            onChange={onChange}
            value={value}
            options={options}
          />
        </ul>
      </div>
    </li>
  );
}


function Instructions({ businessID, followUp }) {
  if (followUp) {
    return (
      <div className="section">
        <p className="item-subtitle">
          Make sure your customers agree to be contacted via SMS (&quot;opt in&quot;),
          for more information, read our <a href={`/${businessID}/account/legal`}>Terms of Service</a> or
           contact <Link href="https://broadlyhelp.force.com/s/contactsupport" external>support</Link> for help.
        </p>
      </div>
    );
  } else {
    return (
      <div className="section">
        <p className="item-subtitle">
          We will only follow up with your customers if we have their email address.
          You can use the app to send text messages directly to their phone.
        </p>
      </div>
    );
  }
}

function EditableUploadImageLineItem({ title, value, save, showToast, upload, onSendPreview }) {
  return (
    <EditableLineItem
      value={value}
      defaultValue={null}
      save={save}
      showToast={showToast}
    >
      <EditableLineItem.Viewing>
        {({ handleStartEditing }) => (
          <EditableLineItem.ListItem
            title={title}
            titleActions={<a onClick={handleStartEditing}>Edit</a>}
            content={<ThankYouImage url={value}/>}
          />
        )}
      </EditableLineItem.Viewing>
      <EditableLineItem.Editing>
        {({ currentValue, handleCancel, handleChange, handleSave, handleUseDefault }) => (
          <UploadImageLineItem
            title={title}
            value={currentValue}
            handleCancel={handleCancel}
            handleChange={handleChange}
            handleUseDefault={handleUseDefault}
            handleSave={handleSave}
            upload={upload}
            showToast={showToast}
            onSendPreview={onSendPreview}
          />
        )}
      </EditableLineItem.Editing>
    </EditableLineItem>
  );
}


function ThankYouImage({ url, blob, mimeType }) {
  const src = (blob && mimeType) ? URL.createObjectURL(blob) : url;

  return (
    <div className="thank-you-image">
      <img src={src}/>
    </div>
  );
}


class UploadImageLineItem extends React.PureComponent {
  constructor(props) {
    super(props);
    const { value } = props;
    const isCustom  = value && !thankYouImagePresets.includes(value);

    this.state = {
      blob:        null,
      mimeType:    null,
      uploadedURL: isCustom ? value : null,
      value
    };

    this.handleCancel              = this.handleCancel.bind(this);
    this.handleError               = this.handleError.bind(this);
    this.handleFileSelected        = this.handleFileSelected.bind(this);
    this.handleSendPreview         = this.handleSendPreview.bind(this);
    this.handleSave                = this.handleSave.bind(this);
    this.handleImageSelectorChange = this.handleImageSelectorChange.bind(this);
    this.doUpload                  = this.doUpload.bind(this);
  }

  async handleSave() {
    await this.doUpload();

    this.props.handleChange(this.state.value);
    await this.props.handleSave();
  }

  async handleSendPreview() {
    const { onSendPreview } = this.props;


    await this.doUpload();

    await onSendPreview(this.state.value);
  }

  async doUpload() {
    const { blob }     = this.state;
    const { mimeType } = this.state;
    const { upload }   = this.props;
    const { value }    = this.state;

    if (value === 'blob') {
      const uploadedURL = await upload({ mimeType, blob });
      this.setState({ uploadedURL, value: uploadedURL, blob: null, mimeType: null });
    }
  }

  handleCancel() {
    this.setState({ blob: null, mimeType: null, uploadedURL: null });
    this.props.handleCancel();
  }

  handleError(error) {
    // Until we upgrade to Babel 7 we can't
    // check with instanceof UnsupportedFileError.
    this.props.showToast(error.message);
  }

  handleFileSelected({ blob, mimeType }) {
    this.setState({ blob, mimeType, uploadedURL: null, value: 'blob' });
  }

  handleImageSelectorChange({ value }) {
    this.setState({ blob: null, mimeType: null, uploadedURL: null, value });
  }

  render() {
    const { blob }        = this.state;
    const { mimeType }    = this.state;
    const { title }       = this.props;
    const { uploadedURL } = this.state;
    const { value }       = this.state;

    return (
      <EditableLineItem.ListItem
        shouldScrollIntoView
        title={title}
        content={
          <React.Fragment>
            <ThankYouImage url={value || uploadedURL} blob={blob} mimeType={mimeType}/>
            <UploadImage
              onSuccess={this.handleFileSelected}
              onError={this.handleError}
            />
            <ThankYouImagePresets
              value={value}
              onChange={this.handleImageSelectorChange}
            />
            <EditableLineItem.Buttons
              handleCancel={this.props.handleCancel}
              handleSave={this.handleSave}
            >
              {() => (
                <a className="button" onClick={this.handleSendPreview}>Send Preview</a>
              )}
            </EditableLineItem.Buttons>
          </React.Fragment>
        }
      />
    );
  }
}


function ThankYouImagePresets({ value, onChange }) {
  return (
    <div className="thank-you-image-presets">
      {thankYouImagePresets.map(presetURL => (
        <div key={presetURL}
          className={(presetURL === value ? 'active' : null)}
          onClick={() => onChange({ value: presetURL })}
        >
          <ThankYouImage url={presetURL}/>
        </div>
      ))}
    </div>
  );
}


export function PhoneNumberSection({ business, hostedNumber, twilioNumber, isHostedSMS }) {
  const usePlural = !isHostedSMS;
  const title     = usePlural ? 'Phone Numbers' : 'Phone Number';

  return (
    <section className="list inset messaging--phonenumber">
      <ul>
        <li className="list-group-title">{title}</li>
        <PhoneNumberDescription {...{ business, hostedNumber, twilioNumber, isHostedSMS }}/>
      </ul>
    </section>
  );
}


function PhoneNumberDescription({ business, twilioNumber, hostedNumber, isHostedSMS }) {
  if (isHostedSMS)
    return <HostedNumberDescription {...{ hostedNumber }}/>;
  else
    return <TwilioNumberDescription {...{ business, twilioNumber, hostedNumber }}/>;
}

function HostedNumberDescription({ hostedNumber }) {
  const { number } = hostedNumber;
  return (
    <li>
      <p>
        The Broadly App will make phone calls and send and receive SMS messages from your number: <FormattedPhone>{number}</FormattedPhone>.
      </p>
    </li>
  );
}

function FormattedPhone({ children }) {
  const formattedNumber = format(children, 'National');
  return (
    <span className="phone-number">{formattedNumber}</span>
  );
}

function TwilioNumberDescription({ business, twilioNumber, hostedNumber }) {
  const businessNumber = business.contact.phone;

  return (
    <React.Fragment>
      <li>
        <p>
          Broadly will make phone calls and send and receive SMS messages from <FormattedPhone>{twilioNumber}</FormattedPhone>.
        </p>
        <p>
          Calls to that number will be forwarded to you at <FormattedPhone>{businessNumber}</FormattedPhone>.
        </p>
      </li>
      {hostedNumber && <HostedOrderDescription {...{ business, hostedNumber }}/>}
    </React.Fragment>
  );
}

function HostedOrderDescription({ business, hostedNumber }) {
  const { status }           = hostedNumber;
  const { errorCode }        = hostedNumber;
  const { failureReason }    = hostedNumber;
  const failure              = (status === 'failed' || errorCode);
  const userFailure          = isUserFailure(failureReason);
  const awaitingVerification = isAwaitingVerification(status);
  const processing           = isProcessing(status);

  if (failure && userFailure)
    return <OrderFailed {...{ business, hostedNumber }}/>;
  else if (awaitingVerification)
    return <OrderAwaitingVerification {...{ business, hostedNumber }}/>;
  else if (processing)
    return <OrderProcessing {...{ business, hostedNumber }}/>;
  else
    return null;
}

function OrderAwaitingVerification({ business, hostedNumber }) {
  const { number } = hostedNumber;
  const { status } = hostedNumber;
  const linkText   = getHostedOrderLinkText(status);

  return (
    <li>
      <p>
        To enable the Broadly App to make phone calls and send and receive SMS messages from <FormattedPhone>{number}</FormattedPhone>, please <Link href={`/${business.id}/settings/messaging/hosted_number`}>{linkText}</Link>.
      </p>
    </li>
  );
}

function getHostedOrderLinkText(status) {
  switch (status) {
    case 'pending-verification':
    case 'verified':
    case 'pending-loa':
      return 'complete your verification';
    default:
      return 'verify your number';
  }
}

function OrderProcessing({ business, hostedNumber }) {
  const { number } = hostedNumber;

  return (
    <li className="messaging--order-status">
      <i className="icon f7-icons info-icon">info_round</i>
      <span>
        We are setting up <FormattedPhone>{number}</FormattedPhone> for phone calls and SMS messages. <Link href={`/${business.id}/settings/messaging/hosted_number`}>Learn more <i className="icon f7-icons arrow-icon">arrow_right</i></Link>
      </span>
    </li>
  );
}

function OrderFailed({ business, hostedNumber }) {
  const { number } = hostedNumber;
  return (
    <li className="messaging--order-status">
      <i className="icon f7-icons alert-icon">alert</i>
      <span>
        There was a problem connecting <FormattedPhone>{number}</FormattedPhone> for phone calls and SMS messages. <Link href={`/${business.id}/settings/messaging/hosted_number`}>Learn more <i className="icon f7-icons arrow-icon">arrow_right</i></Link>
      </span>
    </li>
  );
}

function isUserFailure(failureReason) {
  if (typeof failureReason !== 'string')
    return false;

  const didNotSendLOA           = failureReason.startsWith(twilioFailureReasons.didNotSendLOA);
  const maxVerificationAttempts = failureReason.startsWith(twilioFailureReasons.maxVerificationAttempts);

  return (didNotSendLOA || maxVerificationAttempts);
}

function isAwaitingVerification(status) {
  switch (status) {
    case 'received':
    case 'pending-verification':
    case 'verified':
    case 'pending-loa':
      return true;
    default:
      return false;
  }
}

function isProcessing(status) {
  switch (status) {
    case 'carrier-processing':
    case 'testing':
    case 'action-required':
      return true;
    default:
      return false;
  }
}
